import React, { useEffect, useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { format } from 'date-fns';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils'; // ensure this utility is present or adjust as needed
import { Label } from '@/components/ui/label';
import { supabase } from '@/lib/supabaseClient';
import { Fixture } from '@/types/fixtures';

interface AddHomeFixtureModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  homeTeamId: string;
  divisionId: string;
  seasonId: string;
  teams: { id: string; name: string; division_id: string }[];
  onFixtureAdded: (keepModalOpen?: boolean) => void;
  fixtureToEdit?: Fixture | null;
}

const AddHomeFixtureModal: React.FC<AddHomeFixtureModalProps> = ({
  isOpen,
  onOpenChange,
  homeTeamId,
  divisionId,
  seasonId,
  teams,
  onFixtureAdded,
  fixtureToEdit = null,
}) => {
  const [date, setDate] = useState('');
  const [teeTime, setTeeTime] = useState('17:00');
  const [awayTeamId, setAwayTeamId] = useState('');
  const [addAnother, setAddAnother] = useState(false);
  const [loading, setLoading] = useState(false);
  const [existingFixtures, setExistingFixtures] = useState<Fixture[]>([]);
  const submitButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const loadFixtures = async () => {
      const { data, error } = await supabase
        .from('fixtures')
        .select(`
          id,
          match_date,
          tee_time,
          status,
          completed,
          created_at,
          season_id,
          division_id,
          project_id,
          home_team:home_team_id (
            id,
            name
          ),
          away_team:away_team_id (
            id,
            name
          )
        `)
        .eq('division_id', divisionId)
        .eq('season_id', seasonId);

      if (error) {
        console.error('❌ Error fetching fixtures:', error.message);
      } else {
        const parsedFixtures: Fixture[] = (data ?? []).map((item: any) => ({
          id: item.id,
          match_date: item.match_date,
          tee_time: item.tee_time ?? '17:00',
          status: item.status ?? 'scheduled',
          completed: item.completed ?? false,
          created_at: item.created_at,
          season_id: item.season_id,
          division_id: item.division_id,
          project_id: item.project_id,
          home_team: {
            id: item.home_team?.id ?? '',
            name: item.home_team?.name ?? '',
            division_id: divisionId,
            division: { id: divisionId, name: '' },
          },
          away_team: {
            id: item.away_team?.id ?? '',
            name: item.away_team?.name ?? '',
            division_id: divisionId,
            division: { id: divisionId, name: '' },
          },
        }));

        setExistingFixtures(parsedFixtures);
      }
    };

    loadFixtures();
  }, [isOpen, divisionId, seasonId]);

  useEffect(() => {
    if (fixtureToEdit) {
      const dt = new Date(fixtureToEdit.match_date);
      setDate(dt.toISOString().slice(0, 10));
      setTeeTime(fixtureToEdit.tee_time?.slice(0, 5) ?? '17:00');
      setAwayTeamId(fixtureToEdit.away_team.id);
    } else {
      setDate('');
      setTeeTime('17:00');
      setAwayTeamId('');
    }
  }, [fixtureToEdit, isOpen]);

  const checkDuplicate = async (matchDate: string) => {
    const { data, error } = await supabase
      .from('fixtures')
      .select('id')
      .or(
        `and(home_team_id.eq.${homeTeamId},away_team_id.eq.${awayTeamId},match_date.eq.${matchDate}),` +
        `and(home_team_id.eq.${awayTeamId},away_team_id.eq.${homeTeamId},match_date.eq.${matchDate})`
      );
    if (error) console.error('❌ Error checking duplicates:', error.message);
    return (data ?? []).length > 0;
  };

  const handleSubmit = async () => {
    if (!date || !awayTeamId) return;
    const matchDate = `${date}T${teeTime}:00`;
    setLoading(true);

    if (!fixtureToEdit && (await checkDuplicate(matchDate))) {
      alert('⚠️ A fixture between these two teams already exists on this date.');
      setLoading(false);
      return;
    }

    const payload = {
      match_date: matchDate,
      away_team_id: awayTeamId,
      tee_time: teeTime,
    };

    let error;
    if (fixtureToEdit) {
      ({ error } = await supabase
        .from('fixtures')
        .update(payload)
        .eq('id', fixtureToEdit.id));
    } else {
      const projectId = 'f64fdcde-250b-41d6-ad48-dcc2454fd66c';

      ({ error } = await supabase
        .from('fixtures')
        .insert({
          id: crypto.randomUUID(),
          season_id: seasonId,
          division_id: divisionId,
          home_team_id: homeTeamId,
          project_id: projectId,
          ...payload,
          status: 'scheduled',
          completed: false,
          created_at: new Date().toISOString(),
        }));
    }

    setLoading(false);

    if (!error) {
      onFixtureAdded(addAnother);
      if (!fixtureToEdit && addAnother) {
        setDate('');
        setTeeTime('17:00');
        setAwayTeamId('');
      }
    } else {
      console.error('❌ Error saving fixture:', error.message);
    }
  };

  const unavailableAwayTeamIds = existingFixtures
    .filter(
      (f) =>
        f.season_id === seasonId &&
        f.division_id === divisionId &&
        f.home_team.id === homeTeamId &&
        (!fixtureToEdit || f.id !== fixtureToEdit.id)
    )
    .map((f) => f.away_team.id);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{fixtureToEdit ? 'Edit Fixture' : 'Add Home Fixture'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    'w-full justify-start text-left font-normal',
                    !date && 'text-muted-foreground'
                  )}
                >
                  {date ? format(new Date(date), 'PPP') : 'Pick a date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={date ? new Date(date) : undefined}
                  onSelect={(d) => {
                    if (d) {
                      const localDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
                      setDate(localDate.toLocaleDateString('en-CA')); // YYYY-MM-DD
                    } else {
                      setDate('');
                    }
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          <div>
            <Label>Tee Time</Label>
            <select
              value={teeTime}
              onChange={(e) => setTeeTime(e.target.value)}
              className="w-full border rounded px-3 py-2"
            >
              <option value="17:00">17:00</option>
              <option value="17:30">17:30</option>
            </select>
          </div>

          <div>
            <Label>Away Team</Label>
            <select
              value={awayTeamId}
              onChange={(e) => setAwayTeamId(e.target.value)}
              className="w-full border rounded px-3 py-2"
            >
              {!fixtureToEdit && <option value="">Select a team</option>}
              {teams
                .filter((t) => t.id !== homeTeamId)
                .map((team) => {
                  const isUnavailable = unavailableAwayTeamIds.includes(team.id);
                  const isCurrentAway = fixtureToEdit?.away_team.id === team.id;

                  return (
                    <option
                      key={team.id}
                      value={team.id}
                      disabled={!isCurrentAway && isUnavailable}
                    >
                      {team.name} {!isCurrentAway && isUnavailable ? '– Already scheduled' : ''}
                    </option>
                  );
                })}
            </select>
          </div>

          {!fixtureToEdit && (
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="addAnother"
                checked={addAnother}
                onChange={(e) => setAddAnother(e.target.checked)}
                className="w-4 h-4"
              />
              <Label htmlFor="addAnother">Add another fixture</Label>
            </div>
          )}

          <div className="flex justify-end">
            <Button
              ref={submitButtonRef}
              disabled={loading}
              onClick={handleSubmit}
            >
              {fixtureToEdit ? 'Update Fixture' : 'Add Fixture'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddHomeFixtureModal;